/* eslint-disable no-underscore-dangle */
/* eslint-disable no-restricted-syntax */
import Immutable from "immutable"
import moment from "moment"
import * as Sentry from "@sentry/browser"
import { sheetsTooltips } from "sheets/components/helpTexts"
import { formatNumber as formatNumberHelper } from "shared/imports/sharedHelperFunctions"
import sheetConstants from "sheets/constants"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

export const nullTypes = sheetConstants.FALSEY_VALUES

/**
 * Generate the select options that are relevant given the data in the column
 * @param {Object} field - the field of a model, to get the enum from
 * @param {Map} columnData - the data of the column
 * @returns {Array[Objects]} - An array of filtered enum objects
 */
export const generateRelevantOptions = (field, columnData, hasAllColumnData) => {
    // for sheets
    if (columnData && hasAllColumnData) {
        // collect the enum values we have in the data
        // for backwards compatibility, also check for immutable, since this used to be passed down via props as
        // an Immutable
        const data = columnData.toJS ? columnData.toArray() : Object.values(columnData)
        const enumValues = Immutable.Set(data)
        return DjangIO.enumFromField(field).filter_enums_by_values(enumValues)
    } else {
        // for resource tables, or for big sheets, when don't know the values beforehand
        // allow the creator of the ResourceTable to filter the enum values in the select
        return DjangIO.enumFromField(field).items()
    }
}

/**
 * Given the number of digits present in the last row value, calculate the indeXColumnWidth
 * If enabled, adjust the width to stepwise increase as numDigits increases.
 * If there is only one digit provided, default to having a width with 2 digits.
 * @param {Number} numDigits - the number of digits of last row value, ex. 75 rows -> 2, 100 rows -> 3
 * @param {Bool} isResourceTable
 * @returns {Number} - width of IndexColumn
 */
export const calculateIndexColumnWidth = (numDigits, isResourceTable) => {
    if (isResourceTable) {
        return sheetConstants.RESOURCE_TABLE_INDEX_COLUMN_WIDTH + Math.max(2, numDigits) * 10
    }
    return sheetConstants.INDEX_COLUMN_WIDTH
}

/**
 * Search by an enum's value
 * @param {Type} item - a description
 * @returns {Type} - Something
 */
export const searchByEnum = (item) => item && (item.length ? item.map((i) => i.value) : item.value)

const defaultDisplayFunction = (value) => value

/**
 * This function determines if a field column should be filtered out based on the configuration on the miscellaneous attribute
 * @name shouldIncludeMiscellaneousColumn
 * @function
 * @param {Object} field - a representatation of a field column
 * @param {Integer} dataType - the data type of sheet (QuorumDataType Person, Staffer, etc)
 * @returns {Boolean} - false if this column should be filtered out, true if should stay
 */
export const shouldIncludeMiscellaneousColumn = (field, dataType) => {
    if (!field) {
        return true
    }

    // if the column has no miscellaneous attribute, it should be included (ex. no filtering applied)
    if (!field.miscellaneous) {
        return true
    }

    // configuration is set to be a scalar
    if (field.miscellaneous === dataType) {
        return true
    }

    // if passed an array of miscellaneous data types
    // this corresponds to the sheet types this column should belong on
    // must guard against an object being passed because typeof [] => object
    if (typeof field.miscellaneous === "object" && field.miscellaneous.includes) {
        return field.miscellaneous.includes(dataType)
    }

    return false
}

/**
 * This function applies the display function that may have been provided in the 'display' field
 * in the column configuration of the Resource Table
 * The arguments to the display function will be the strings provided in the 'field' key
 * An additional object of arguments is passed as the last argument of the display function
 * @param {List|String} columnField - The field(s) to access in the returned data value
 * @param {Map} row - The particular row of the sheet. It is the data returned by the API for a given id
 * @param {Integer} rowIndex - The index in the row_data List that is internal to sheets
 * @param {Function} optionalActionCreator - an optional action creator to be used to make network requests instead of default
 * @param {Function} display=defaultDisplayFunction - Provide the ability to custom the display value of a cell
 * @returns {Component|String} - Either a React component or a string to render
 */
export const processCellValue = (cellArgs) => {
    const { columnField, row, rowIndex, sheetKey, columnIndex, optionalActionCreator } = cellArgs
    const display = cellArgs.display || defaultDisplayFunction
    // if there are multiple fields required by this column
    if (columnField instanceof Immutable.List) {
        // pass them all to the display function
        return display(...columnField.map((field) => row.get(field)), {
            columnIndex,
            optionalActionCreator,
            rowIndex,
            tableId: sheetKey,
        })
    }

    // otherwise just pass the single field
    return display(row.get(columnField), {
        rowIndex,
        tableId: sheetKey,
        columnIndex,
    })
}

// ------------------------- BEGIN COLUMNFORM HELPERS ------------------------------ //

/**
 * Generate the options to provide the GridToggle component options to render
 * Eliminates unnescessary fields and searches for related fields on foreign keys
 * @param {integer} dataType The resource datatype
 * @returns {Array<Objects>} - The fields that act like columns, in the same format as SheetColumnType enum
 */
export const extractFieldsFromResourceIncludingRelated = (dataType) =>
    // this resources's fields
    DjangIO.resourceFromDataType(dataType)
        ._meta.fields // exclude timestamps, except if we are dealing with a supporter
        // in the future we should combine all of these inclusions/exclusions as the number of
        // edge cases grow
        .filter(
            (field) =>
                dataType === DjangIO.app.models.QuorumDataType.supporter.value ||
                !["created", "updated"].includes(field.name),
        )
        .filter((field) => field.sheet_column)

        // handle foreign keys
        .reduce(
            // at each step, accept the array of fields and the next field to add
            (acc, nextField) =>
                // if it's a foreign key
                [
                    DjangIO.djangio.types.FieldType.ForeignKey.value,
                    DjangIO.djangio.types.FieldType.ManyToManyField.value,
                ].includes(nextField.field_type)
                    ? // add all of the related models' fields so we can access them
                      acc.concat(
                          DjangIO.resourceFromRelatedModel(nextField.related_model)
                              ._meta.fields // exclude dates
                              // only include fields that are supposed to be shown in sheets
                              // wrapping a field on the model in a PublicField(...) accomplishes this
                              .filter((field) => !["created", "updated"].includes(field.name) && field.sheet_column)

                              // also check to see if we want this related field
                              // we needed to add this check because fields started to be shared across sheets
                              // if we haven't defined an array includedRelatedModelFields as an attribute on the
                              // the PublicField on the backend, default to including all of the fields
                              .filter((field) =>
                                  nextField.included_related_model_fields
                                      ? nextField.included_related_model_fields.includes(field.name)
                                      : true,
                              )
                              .map((field) => ({
                                  // default to the values on this model
                                  ...nextField,
                                  // override with the values on the related model
                                  ...field,
                                  // join with __ so that the grid toggle stores
                                  // the correct lookup in the database
                                  name: `${nextField.name}__${field.name}`,
                                  // also need to keep the miscellaneous of the base pub field
                                  miscellaneous: field.miscellaneous,
                              })),
                      )
                    : // otherwise just add it normally
                      acc.concat([nextField]),
            [],
        )
        // If a miscellaneous data type is defined, only apply the field to that (useful for person type => officials and sta)
        .filter((field) => shouldIncludeMiscellaneousColumn(field, dataType))
        .map((field) => ({
            ...field,
            value: field.name,
            tip: field.sheets_tooltip_text || field.help_text,
            label: field.verbose_name,
        }))
        .sort(DjangIO.sortByKey("sheets_column_order"))

/** get the legislation enum options for a GridToggle in the ColumnForm
 * @returns {Object} slightly formated DjangIO enums
 */
export const getLegislationOptions = () => {
    const keys = [
        DjangIO.app.models.QuorumDataType.bill.key,
        DjangIO.app.models.QuorumDataType.vote.key,
        DjangIO.app.models.QuorumDataType.amendment.key,
    ]
    return DjangIO.app.models.QuorumDataType.items()
        .filter((item) => keys.includes(item.key))
        .map((field) => ({
            ...field,
            tip: sheetsTooltips.legislation[field.key] || "",
        }))
}

/**
 *  Only true if FF is enabled, or if no FF slug provided.
 *
 * @param type
 * @returns {bool}
 */
export const isColumnTypeFeatureFlagSlugEnabled = (type) => {
    const slug = type.feature_flag_slug
    return !slug || isFeatureEnabled(slug)
}

/**
 * Generate the object to pass to editColumnForm when YearSelect changes.
 *
 * @param {Array | number | undefined} transactionYears - The changed result of a YearSelect
 * @returns {Object} - An object that can be merged into the campaign form to update the transaction years
 */
export const yearSelectChange = (transactionYears) => {
    if (!transactionYears) {
        return { transactionYears: undefined }
    } else if (Array.isArray(transactionYears)) {
        return { transactionYears: transactionYears.map((yearObj) => Number(yearObj.value)) }
    } else {
        return { transactionYears: [Number(transactionYears.value)] }
    }
}

// --------------------------- END COLUMNFORM HELPERS --------------------------- //

/**
 * Llts of work needs to be done to get null_cell_value due to implicit columnTypes due to publicfield
 * This goes and grabs the potential null value a cell would have, for all public fields and column type
 * @param {integer} dataType - the sheet's data type (QuorumDataType)
 * @param {integer} columnType - the column's type (SheetColumnType)
 * @param {string} columnField - the column's field (for field ColumnTypes)
 * @returns {string} - the null value to be rendered
 */
export const getNullValueForColumn = (dataType, columnType, columnField) => {
    let null_value = null
    if (columnType === DjangIO.app.userdata.spreadsheet.types.SheetColumnType.field.value) {
        const fields = extractFieldsFromResourceIncludingRelated(dataType)
        fields.map((e) => {
            if (e.name === columnField) {
                null_value = e.null_cell_value
            }
        })
    } else {
        const sheetColumnType = DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(columnType)
        if (sheetColumnType) {
            null_value = sheetColumnType.null_cell_value
        }
    }
    return null_value
}

/**
 * This function formats a number for a Sheet Cell
 * @name formatNumber
 * @function
 * @param {Number} rawNumber - a number to format
 * @param {Boolean} shouldRoundCellValue - Whether or not we should round the number
 * @returns {String} - Formatted number
 */
export const formatNumber = (rawNumber, shouldRoundCellValue = false) => {
    return formatNumberHelper(rawNumber, shouldRoundCellValue)
}

/**
 * Given a collection of values and a number, determine the style that applies for that never based on
 * where the number falls in the left bound range
 * Requires a sorted Immutable OrderedMap
 * @name someName
 * @function
 * @param {OrderedMap} mapping - an OrderedMap where keys represents left bound of range, and value represents
 * the color applicable to that range
 * @param {String|Number} value - the value we want to find the corresponding color value for
 * @returns {String|undefined} value - a color that represents the range the value falls in, or undefiend if number does not fal
 * in any range
 */
export const computeLeftBoundRange = ({ mapping, value, comparingDates = false }) => {
    if (!mapping || !mapping.size) {
        return undefined
    }

    if (value === "") {
        return mapping.get("")
    }

    if (comparingDates && !value._isAMomentObject) {
        value = moment(value).utc()
    }
    let previousBound
    let previousBoundStyle

    // since there is a blank ("") value in the mapping, it messes up computing the lower bound so we need to remove it
    const nonBlankMapping = mapping.filter((_, ind) => ind !== "")

    // The mapping must be sorted by key (according to key datatype) since
    // the key holds the comparison condition, not the value
    const nonBlankOrderedMapping = comparingDates
        ? nonBlankMapping.sortBy((value, key) =>
              key._isAMomentObject
                  ? // convert dates to moments if they aren't already
                    key
                  : moment(key).utc(),
          )
        : nonBlankMapping.sortBy((value, key) =>
              // ensure the keys are numbers for comparison, not strings
              Number(key),
          )

    // we need a for of loop so we can break early. It is unlikely for mapping to be more than a few dozen entries
    for (let [key, style] of nonBlankOrderedMapping) {
        if (comparingDates && !key._isAMomentObject) {
            key = moment(key).utc()
        }
        // the number is smaller than the first value. No color should be applied
        if (!previousBound && value < key) {
            return undefined
        }
        if (value < key) {
            // the value is between the previous and the current. Use the previous color
            return previousBoundStyle
        } else if (value === key) {
            // since we are left bounded, use the current styl
            return style
        }

        previousBound = key
        previousBoundStyle = style
    }
    if (!previousBound) {
        return undefined
    } else {
        return previousBoundStyle
    }
}

/**
 * A rather naive check to determine if two Lists of columns are equal
 * This check is used to determine if it is necessary to update the Sheet
 * Use the name of the column to determine if a column is equal to another column
 * @name areSheetColumnsEqual
 * @function
 * @param {Immutable.List} firstList - a List of columns
 * @returns {Immutable.List} secondList - a List of columns
 */
export const areSheetColumnsEqual = (firstList, secondList) => {
    let _firstList
    let _secondList
    if (!firstList && !secondList) {
        return false
    }
    // Make sure always comparing Immutable
    if (firstList.constructor !== Immutable.List) {
        _firstList = Immutable.fromJS(firstList).sortBy((col) => col.get("name"))
    } else {
        _firstList = firstList.sortBy((col) => col.get("name"))
    }

    if (secondList.constructor !== Immutable.List) {
        _secondList = Immutable.fromJS(secondList).sortBy((col) => col.get("name"))
    } else {
        _secondList = secondList.sortBy((col) => col.get("name"))
    }

    if (_firstList.size !== _secondList.size) {
        return false
    }

    for (let i = 0; i < _firstList.size; i++) {
        if (_firstList.getIn([i, "name"]) !== _secondList.getIn([i, "name"])) {
            return false
        }
    }
    return true
}

/**
 * This function determines if a particular row should be filtered out by the user's search
 * in a column.
 *
 * @name filterRowByCustomFieldColumn
 * @function
 *
 * @param {object} args - an object containing the args
 * @param {List} args.searchingColumnValue - a String representing the search value of a column
 * @param {String|List[String]} args.columnValue - the cell value. Supported types are String slugs or a list of slugs
 * @returns {Boolean} - if true, the column should not be filtered out. false if should be
 * Example: User is searching 'Apple' in a Select Many Custom Field Column
 * The Custom Field Choose Many has options:
 *  - Honey Crisp
 *  - Gala
 *  - Red Delicious
 *
 *  Sheet has 3 rows, Lamar, Tammy, and Paul
 *  The cell for Lamar has the values Honey Crisp
 *  The cell for Tammy has Gala, Honey Crisp
 *  The cell for Paul empty
 *  This function will return true for lamar, true for Tammy, and false for Paul
 *  Notes:
 *      - this function handles both single and select many custom field options
 *          - in the case of single selects, the columnValue is a string
 *      - We do not call toLowerCase() because the slugs should be matched on exactly
 *      - Custom Fields are a system. Columns that are considered Custom Field columns are of type CustomTagColumn
 */
export const filterRowByCustomFieldColumn = ({ searchingColumnValue, columnValue }) => {
    // falsy searchiningColumnValue means no filter is being applied
    if (!searchingColumnValue || searchingColumnValue.size === 0) {
        return true
    }

    // ruling out empty filtering, if the cell value is empty, filter it out
    if (!columnValue || columnValue.size === 0) {
        return false
    }

    // Select One Custom Field
    if (typeof columnValue === "string") {
        return searchingColumnValue.includes(columnValue)
    } else if (columnValue.constructor === Immutable.List) {
        // Select Many custom field - columnValue is actaully a List
        return Boolean(columnValue.filter((slug) => searchingColumnValue.includes(slug)).size)
    } else if (columnValue.constructor === Immutable.Map) {
        const value = columnValue.get("value")
        if (!value) {
            return false
        }
        if (value.constructor === Immutable.List) {
            return !!Immutable.Set(value).intersect(Immutable.Set(searchingColumnValue)).size
        } else {
            return searchingColumnValue.includes(value)
        }
    }

    return true
}

/**
 * This function determines if a boolean value row should be filtered out by a true ("yes")
 * or false ("no") search value.
 *
 * Example: User has a custom field - "Is this bill active?"
 *    - searchingColumnValue: The user selects true (wants to view only active bill rows)
 *    - columnValue: Bill 1 is false, Bill 2 is true, Bill 3 is false
 *    - The function will return false for Bill 1, true for Bill 2, and false for Bill 3
 *
 * @name filterRowByBooleanCustomFieldColumn
 * @function
 * @param {object} args - an object containing the args
 * @param {Boolean} args.searchingColumnValue - the value filtered by
 * @param {Boolean|null} args.columnValue - the value the search value is compared to
 * @returns {Boolean} - whether or not the column value matches the searching column value
 */
export const filterRowByBooleanCustomFieldColumn = ({ searchingColumnValue, columnValue }) => {
    // null represents a blank cell and the only filtering options are true or false so it will never be a match
    if (columnValue === null) {
        return false
    }

    if (typeof columnValue === "object" && columnValue !== null && "value" in columnValue.toJS()) {
        columnValue = columnValue.get("value")
    }

    return searchingColumnValue === columnValue
}

/**
 * This function determines if a particular row should be filtered out by the user's search
 * in a column.
 *
 * @name filterRowByStringColumn
 * @function
 * @param {object} args - an object containing the args
 * @param {Type} args.searchingColumnValue - a String representing the search value of a column
 * @param {String|List[Map]} args.columnValue - the cell value. Supported types are a String, or a list of Maps
 * @returns {Boolean} - if true, the column should not be filtered out. false if should be
 * Example: User is searching 'ame' in an All Lists column
 *  Sheet has 2 rows, Lamar and Tammy
 *  The cell for Lamar has the list america, while the cell for Tammy is empty
 *  This function will return true for lamar, false for Tammy, because 'ame' matches for the cell
 *  value in Lamar's row
 */
export const filterRowByStringColumn = ({ searchingColumnValue, columnValue }) => {
    // do not filter in these cases, leave out falsy values like 0
    if (searchingColumnValue === undefined || searchingColumnValue === null || searchingColumnValue === "") {
        return true
    }

    // ruling out falsy searchingColumnValues, now safe to return false for falsy columnValues
    if (columnValue === undefined || columnValue === null) {
        return false
    }

    if (typeof columnValue === "string") {
        return columnValue.toLowerCase().includes(searchingColumnValue.toLowerCase())
    }

    // we have introduced some cases where the cell value is a List of objects, rather than a string
    // attempt to handle this situation gracefully by introspecting into the keys available in the objects,
    // other than the 'id'
    // this situation is encountered when the cell needs to have editable functionality, such a list of Issues
    // that a row is associated with.
    if (columnValue.constructor === Immutable.List) {
        try {
            // case where cell is empty
            if (!columnValue.size) {
                return false
            }

            // introspect keys, attempt to match the 'representing' key of each item in the list of values
            const filteredColumnValues = columnValue
                .filter((val) => val !== undefined || val !== null)
                .filter((val) => val.size !== 0) // even if not immutable, val.size evaluates to undefined
                .filter((val) => val !== "")

            // ex: [{}] or [""] - this is equivalent to an empty cell
            if (!filteredColumnValues.size) {
                return false
            }

            // handle the case where we are passed an Immutable List of Strings
            if (typeof filteredColumnValues.first() === "string") {
                const intermediate = columnValue.filter((item) =>
                    String(item).toLowerCase().includes(String(searchingColumnValue).toLowerCase()),
                )
                return intermediate.size >= 1
            }

            const keysInFirstItem = filteredColumnValues.first().keySeq().sort().toArray()

            let key
            if (keysInFirstItem.includes("name")) {
                key = "name"
            } else if (keysInFirstItem.includes("label")) {
                key = "label"
            } else {
                // no match available, just take the first key that is not "id"
                key = keysInFirstItem.filter((k) => k !== "id")[0]
            }

            // determine if there are elements that match
            // an element that passes the filter condition indicates that one of the objects matches the search value
            // based on the value in its key
            const intermediate = columnValue.filter((item) =>
                String(item.get(key)).toLowerCase().includes(String(searchingColumnValue).toLowerCase()),
            )
            return intermediate.size >= 1
        } catch (error) {
            // somehow we errored in filtering, default to including the row
            console.error(error)
            Sentry.captureException(error)
            return true
        }
    }

    return true
}

/**
 * Helper to check if value is in an item or equal to it (useful for columnEnums that have arrays or single items as attrs)
 */
export const isOrIn = (obj, value) => {
    if (!obj || !value) {
        return false
    }
    return typeof obj === "object" ? obj.includes(value) : obj === value
}

// define which configuration fields should be passed down via the column prop
// Prevent users from accidently passing down props that can interfere with Sheets functioning properly
// TODO Apply this to every place a column configuration is used. Currently, this only
// applies for the column prop passed to SheetCell
const resourceTableColumnConfigurationFields = ["enumLabel"]

/**
 * Given a datum and dataType, determine if the DatumCell needs to be highlighted
 * for a specific row in a resource table.
 * @name shouldHighlightDatumCell
 * @function
 * @param {Object} datum - Data for a specific row of a Resource Table
 * @param {Number} dataType - Associated QuorumDataType of the Resource Table
 * @returns {Boolean} - Determines whether the Datum Text should be bolded.
 */
export const shouldHighlightDatumCell = (datum, dataType) => {
    switch (dataType) {
        case DjangIO.app.models.QuorumDataType.alert.value:
            return datum.get("paused") === false
        case DjangIO.app.models.QuorumDataType.feature_flag.value:
            return datum.get("is_enabled_for_user")
        default:
            return false
    }
}

/**
 * Get a subset of fields from a Column Map
 * @name getFieldsFromColumnObject
 * @function
 * @param {Map} col - A column
 * @param {Boolean} isResourceTable - Whether or not we are rendering a Resource Table
 * @returns {Object} - an object that has a subset of fields from a Column
 */
export const getFieldsFromColumnObject = (col, isResourceTable) => {
    const obj = {
        columnId: col.get("id"),
        columnWidth: col.get("width", sheetConstants.DEFAULT_COLUMN_WIDTH),
        derived: col.get("column_type")
            ? DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(col.get("column_type")).derived
            : false,
        should_hide_column_on_public: col.get("should_hide_column_on_public"),
        columnType: col.get("column_type"),
    }
    if (isResourceTable) {
        return resourceTableColumnConfigurationFields.reduce(
            (acc, field) => (col.get(field) ? { ...acc, [field]: col.get(field) } : acc),
            obj,
        )
    }
    return obj
}

/**
 * Comparison method handles cases where at least 1 of a,b is a nullType
 * @param {*} a
 * @param {*} b
 * @returns {Integer} - It's 1 if 'a' is nullType, -1 if 'b' is nullType and 0 if both are nullType
 */
export const handleNullTypes = (a, b) => {
    // if a is one of '', null, or undefined, and b isn't, treat a as larger so it is always lower in sheets than any
    // other rows we do this since we want blank cells to appear below cells with values in a column
    if (nullTypes.includes(a) && !nullTypes.includes(b)) {
        return 1
    }
    // if b is one of '', null, or undefined, and a isn't, treat b as larger
    if (nullTypes.includes(b) && !nullTypes.includes(a)) {
        return -1
    }
    // if both are of '', null, or undefined
    return 0
}

/**
 * Comparator function for currency strings.
 *
 * @param {object} args - an object containing the args
 * @param {Boolean} args.shouldReverse if truthy, will return a comparator value for descending order. Else, ascending.
 * @returns {Number}
 */
export const sortCurrencyFn =
    ({ shouldReverse }) =>
    (a, b) => {
        if (!nullTypes.includes(a) && !nullTypes.includes(b)) {
            return shouldReverse ? b - a : a - b
        } else {
            return handleNullTypes(a, b)
        }
    }

/**
 * Filter currency within a range.
 *
 * Assumes the input min: `1` and max: `3` would, for example, be valid for $2.00.
 *
 * @param {object} args - an object containing the args
 * @param {Number} args.maximum
 * @param {Number} args.minimum
 * @param {Number} args.value currency in terms of cents
 * @returns {Boolean}
 */
export const filterCurrencyFn = ({ maximum, minimum, value }) => {
    if (value === null) {
        return false
    }
    const numericalValue = value / 100 // Assuming USD. Refactor if using currency with more than two decimal places.
    return numericalValue >= Number(minimum) && numericalValue <= Number(maximum)
}

/**
 * Comparator function for two strings
 *
 * @param {object} args - an object containing the args
 * @param {Boolean} args.shouldReverse if truthy, will return a comparator value for descending order. Else, ascending.
 * @returns {Integer}
 */

export const stringSort =
    ({ shouldReverse }) =>
    (a, b) => {
        if (!nullTypes.includes(a) && !nullTypes.includes(b)) {
            return shouldReverse ? String(a).localeCompare(String(b)) : String(b).localeCompare(String(a))
        } else {
            return handleNullTypes(a, b)
        }
    }

/**
 * Comparator function for two integers
 *
 * @param {object} args - an object containing the args
 * @param {boolean} args.shouldReverse {Boolean} shouldReverse if truthy, will return a comparator value for descending order. Else, ascending.
 * @returns {Integer}
 */

export const integerSort =
    ({ shouldReverse }) =>
    (a, b) => {
        if (!isNaN(a) && !isNaN(b) && !nullTypes.includes(a) && !nullTypes.includes(b)) {
            const intA = parseInt(a)
            const intB = parseInt(b)

            return shouldReverse ? (intA > intB ? -1 : 1) : intA > intB ? 1 : -1
        } else {
            // Handle null values or non-integer values here
            return handleNullTypes(a, b)
        }
    }

/**
 * Comparator function for Bill Numbers
 *
 * @param {object} args - an object containing the args
 * @param {Boolean} args.shouldReverse if truthy, will return a comparator value for descending order. Else, ascending.
 * @returns {Integer}
 */
export const billNumberSort =
    ({ shouldReverse }) =>
    (a, b) => {
        // Bill numbers are strings that contain the type and number
        // For instance, H.R. 123 is House Resolution 123. When sorting,
        // we first sort by the abbreviation then the number
        const billNumberRegex = /(([A-Za-z]|\.)*) ?(\d*)/
        const matchesA = a.match(billNumberRegex)
        const matchesB = b.match(billNumberRegex)

        if (matchesA === null || matchesB === null) {
            return shouldReverse ? stringSort({ shouldReverse: true }) : stringSort({})
        }

        const typeA = matchesA[1]
        const numA = matchesA[3]
        const typeB = matchesB[1]
        const numB = matchesB[3]

        if (typeA !== typeB) {
            return shouldReverse ? stringSort({ shouldReverse: true })(typeA, typeB) : stringSort({})(typeA, typeB)
        } else {
            return (shouldReverse ? 1 : -1) * Math.sign(parseInt(numA, 10) - parseInt(numB, 10))
        }
    }

/**
 * Comparator for two Congressional districts
 *
 * @param {object} args - an object containing the args
 * @param {Boolean} args.shouldReverse if truthy, will return a comparator value for descending order. Else, ascending.
 * @returns {Integer}
 */
export const congressionalDistrictSort =
    ({ shouldReverse }) =>
    (a, b) => {
        // Congressional districts are strings that contain the district name / number followed by the state
        // E.g.: "Congressional District 4, Indiana"
        // sort by the state first, then the district
        // this prevents grouping by district number without regard to state,
        // as well as sorting numbers lexicographically, which is unintuitive

        // some officials have no district, and so a or b may be undefined
        // put all undefined districts at the end of the sort (both asc. and desc.)
        if (!a) {
            return 1
        }
        // a defined and b undefined, put a first
        if (!b) {
            return -1
        }

        // break the district name up into [(full match), district name, number, comma, state]
        const congressionalDistrictRegex = /(.*[^0-9]+)([0-9]*)( *, *)(.+)/
        const matchesA = a.match(congressionalDistrictRegex)
        const matchesB = b.match(congressionalDistrictRegex)

        if (matchesA === null || matchesB === null) {
            return shouldReverse ? stringSort({ shouldReverse: true }) : stringSort({})
        }

        // if we don't have the expected number of groups, then the format is unexpected
        // just use stringSort
        if (matchesA.length < 5 || matchesB.length < 5) {
            return shouldReverse ? stringSort({ shouldReverse: true }) : stringSort({})
        }

        const districtA = matchesA[1]
        var numberA = matchesA[2]
        const stateA = matchesA[4]
        const districtB = matchesB[1]
        var numberB = matchesB[2]
        const stateB = matchesB[4]

        // determine if there are district numbers
        var numbers_present = false
        if (numberA.length !== 0 && numberB.length !== 0) {
            numbers_present = true
            numberA = parseInt(numberA, 10)
            numberB = parseInt(numberB, 10)
        }

        // if the states are the same, sort by district number
        // otherwise, sort by state name
        if (stateA === stateB) {
            // if the districts are the same, sort by district number if possible
            if (districtA === districtB) {
                if (numbers_present) {
                    return (shouldReverse ? 1 : -1) * Math.sign(numberA - numberB)
                }
            }
            // sort by district name if they are unequal or there are no numbers
            return shouldReverse
                ? stringSort({ shouldReverse: true })(districtA, districtB)
                : stringSort({})(districtA, districtB)
        } else {
            return shouldReverse ? stringSort({ shouldReverse: true })(stateA, stateB) : stringSort({})(stateA, stateB)
        }
    }

/**
 * Comparator function for two dates
 * @param {object} args - an object containing the args
 * @param {Boolean} args.shouldReverse if truthy, will return a comparator value for descending order. Else, ascending.
 * @returns {Integer}
 */
export const dateSort =
    ({ shouldReverse }) =>
    (a, b) => {
        // if neither is null
        if (!nullTypes.includes(a) && !nullTypes.includes(b)) {
            return shouldReverse ? (moment(a) > moment(b) ? -1 : 1) : moment(a) > moment(b) ? 1 : -1
        } else {
            return handleNullTypes(a, b)
        }
    }

export const getPartyKey = (person) =>
    person.get("most_recent_party")
        ? DjangIO.app.person.types.Party.by_value(person.get("most_recent_party")).key.toLowerCase()
        : DjangIO.app.person.types.Party.Independent.key.toLowerCase()

/**
 * Returns the proper value of the enum object.
 * If the value is true/false or a truthy value, we want these actual values
 * If the value doesn't exist or is a falsey value that's not false we'll return null
 *
 * @name getEnumValueForPatch
 * @function
 * @param {Object} enumObj
 * @returns {number|string|null} the cell value to render
 */
export const getEnumValueForPatch = (enumObj) => {
    if (enumObj && !sheetConstants.FALSEY_VALUES.includes(enumObj.value)) {
        return enumObj.value
    }
    return (enumObj && enumObj.value) || null
}

/**
 *
 * @param {List} assignees List that represents assigned people
 * @returns {String} fullname of the Primary assignee
 * Because the primary assignee is the first on this list, we generate the fullname using the first peron's first and last name
 */
export const getPrimaryAssigneeFullName = (assignees) =>
    assignees && assignees.size
        ? `${assignees.getIn([0, "recipient__first_name"], "")} ${assignees.getIn([0, "recipient__last_name"], "")}`
        : ""

/**
 * Grabs the "value" from a JSON object generated from a column's generate method
 *    Ex: {"id": {"some_other_info": "abc", "cell_value": "xyz"}} -> returns "xyz"
 *
 * @function
 * @param {Map} column a column in the sheet
 * @returns {Map} the data, in the form of a Map of k/v pairs where the key is the entity id
 */
export const getValuesFromJSONData = (column) => column.get("data").map((i) => i.get("cell_value"))
/*
 * If the columnIndex is greater than half the total number of columns then render hovertext left. Else, right.
 *
 * @function
 * @param {Number} columnCount the total number of columns in a sheet
 * @param {Number} columnIndex the index of the calling column component
 * @returns {String} calculatedPosition string. Either left or right.
 */
export const calculateHoverTextPosition = (columnCount, columnIndex) => {
    let calculatedPosition = ""
    if (columnIndex + 1 >= (columnCount + 1) / 2) {
        calculatedPosition = "left"
    } else {
        calculatedPosition = "right"
    }
    return calculatedPosition
}

/**
 * Return SafedSearchManager.create kwargs with merged `created_region` values.
 *
 * @function
 * @param {Object} currentSavedSearch the current SafedSearch object
 * @param {Object} nextSavedSearchArgs the args to post for the next SafedSearch creation
 * @returns {Object} Recomputed postSavedSearchKwargs with merged SavedSearch.created_regions
 */
export const mergeSavedSearchRegions = (currentSavedSearch, nextSavedSearchArgs) => {
    const previousRegions = currentSavedSearch?.get("created_regions", []) ?? []
    const nextRegions = nextSavedSearchArgs?.created_regions ?? []
    const newMergedRegions = Array.from(new Set([...nextRegions, ...previousRegions])).sort((a, b) => a - b)

    return {
        ...nextSavedSearchArgs,
        created_regions: newMergedRegions,
    }
}
